import { useState, useMemo, useCallback } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import type { SessionSummary } from '@/types/telemetry';
import { isSessionWet } from '@/lib/weather';
import { formatTime } from '@/lib/format';
import { useI18n } from '@/context/I18nContext';

interface SessionSelectorProps {
  sessions: SessionSummary[];
  selectedIds: Set<string>;
  onSelectionChange: (ids: Set<string>) => void;
  driverFilter: string;
  onDriverFilterChange: (driver: string) => void;
  weatherFilter: string;
  onWeatherFilterChange: (filter: string) => void;
  uniqueDrivers: string[];
}

interface DateGroup {
  dateLabel: string;
  sessions: SessionSummary[];
}

export function SessionSelector({
  sessions,
  selectedIds,
  onSelectionChange,
  driverFilter,
  onDriverFilterChange,
  weatherFilter,
  onWeatherFilterChange,
  uniqueDrivers,
}: SessionSelectorProps) {
  const { t } = useI18n();
  const [collapsedDates, setCollapsedDates] = useState<Set<string> | null>(null);

  const filteredSessions = useMemo(() => {
    return sessions.filter(s => {
      if (driverFilter !== 'all' && s.driverName !== driverFilter) return false;
      if (weatherFilter === 'dry' && isSessionWet(s.weather)) return false;
      if (weatherFilter === 'wet' && !isSessionWet(s.weather)) return false;
      return true;
    });
  }, [sessions, driverFilter, weatherFilter]);

  const dateGroups = useMemo<DateGroup[]>(() => {
    const groups: Record<string, SessionSummary[]> = {};
    for (const s of filteredSessions) {
      const date = s.date || 'Unknown';
      if (!groups[date]) groups[date] = [];
      groups[date].push(s);
    }
    return Object.entries(groups)
      .sort(([a], [b]) => b.localeCompare(a))
      .map(([dateLabel, sessions]) => ({ dateLabel, sessions }));
  }, [filteredSessions]);

  // Default all date groups to collapsed
  const effectiveCollapsed = useMemo(() => {
    if (collapsedDates !== null) return collapsedDates;
    return new Set(dateGroups.map(g => g.dateLabel));
  }, [collapsedDates, dateGroups]);

  const toggleDate = useCallback((date: string) => {
    setCollapsedDates(_prev => {
      const base = _prev ?? new Set(dateGroups.map(g => g.dateLabel));
      const next = new Set(base);
      next.has(date) ? next.delete(date) : next.add(date);
      return next;
    });
  }, [dateGroups]);

  const toggleSession = useCallback((id: string) => {
    onSelectionChange(new Set(
      selectedIds.has(id)
        ? [...selectedIds].filter(i => i !== id)
        : [...selectedIds, id]
    ));
  }, [selectedIds, onSelectionChange]);

  const toggleDateGroup = useCallback((group: DateGroup) => {
    const allSelected = group.sessions.every(s => selectedIds.has(s.id));
    const newSet = new Set(selectedIds);
    for (const s of group.sessions) {
      allSelected ? newSet.delete(s.id) : newSet.add(s.id);
    }
    onSelectionChange(newSet);
  }, [selectedIds, onSelectionChange]);

  const handleSelectAll = useCallback(() => {
    const allSelected = filteredSessions.every(s => selectedIds.has(s.id));
    if (allSelected) {
      onSelectionChange(new Set());
    } else {
      onSelectionChange(new Set(filteredSessions.map(s => s.id)));
    }
  }, [filteredSessions, selectedIds, onSelectionChange]);

  const allSelected = filteredSessions.length > 0 && filteredSessions.every(s => selectedIds.has(s.id));

  return (
    <div className="card overflow-hidden">
      {/* Filters + count */}
      <div className="flex items-center gap-2 px-4 py-3 border-b border-border-subtle bg-bg-surface/50">
        <select
          value={driverFilter}
          onChange={e => onDriverFilterChange(e.target.value)}
          className="flex-1 text-xs py-1.5 px-2.5 rounded-lg bg-bg-surface border border-border-subtle text-text-secondary focus:border-accent outline-none"
        >
          <option value="all">{t('sessionSelector.allDrivers')}</option>
          {uniqueDrivers.map(d => <option key={d} value={d}>{d}</option>)}
        </select>
        <select
          value={weatherFilter}
          onChange={e => onWeatherFilterChange(e.target.value)}
          className="flex-1 text-xs py-1.5 px-2.5 rounded-lg bg-bg-surface border border-border-subtle text-text-secondary focus:border-accent outline-none"
        >
          <option value="all">{t('sessionSelector.allWeather')}</option>
          <option value="dry">{t('sessionSelector.dryOnly')}</option>
          <option value="wet">{t('sessionSelector.wetOnly')}</option>
        </select>
        <span className="text-[0.68rem] text-text-muted whitespace-nowrap">
          {selectedIds.size}/{filteredSessions.length}
        </span>
      </div>

      {/* Select all */}
      <div
        className="flex items-center gap-2.5 px-4 py-2 cursor-pointer hover:bg-bg-hover border-b border-border-subtle transition-colors"
        onClick={handleSelectAll}
      >
        <input
          type="checkbox"
          checked={allSelected}
          onChange={handleSelectAll}
          className="w-3.5 h-3.5 accent-accent cursor-pointer"
          onClick={e => e.stopPropagation()}
        />
        <span className="text-xs text-text-muted">
          {allSelected ? t('sessionSelector.deselectAll') : t('sessionSelector.selectAll')}
        </span>
      </div>

      {/* Session tree */}
      <div className="max-h-[280px] overflow-y-auto">
        {dateGroups.map(group => {
          const collapsed = effectiveCollapsed.has(group.dateLabel);
          const groupAllSelected = group.sessions.every(s => selectedIds.has(s.id));
          const groupSomeSelected = group.sessions.some(s => selectedIds.has(s.id));
          const dayBestMs = Math.min(...group.sessions.map(s => s.bestLapTimeMs).filter(t => t > 0));

          return (
            <div key={group.dateLabel} className="border-b border-border-subtle last:border-b-0">
              <div
                className="flex items-center gap-2 px-4 py-2 cursor-pointer hover:bg-bg-hover transition-colors select-none"
              >
                <input
                  type="checkbox"
                  checked={groupAllSelected}
                  ref={el => { if (el) el.indeterminate = groupSomeSelected && !groupAllSelected; }}
                  onChange={() => toggleDateGroup(group)}
                  className="w-3.5 h-3.5 accent-accent cursor-pointer shrink-0"
                  onClick={e => e.stopPropagation()}
                />
                <div
                  className="flex items-center gap-2 flex-1"
                  onClick={() => toggleDate(group.dateLabel)}
                >
                  <span className="text-text-muted text-[0.7rem] w-3 text-center transition-transform">
                    {collapsed ? <ChevronRight size={12} /> : <ChevronDown size={12} />}
                  </span>
                  <span className="text-[0.8rem] font-semibold text-text-primary">{group.dateLabel}</span>
                  <span className="ml-auto flex items-center gap-3">
                    {isFinite(dayBestMs) && (
                      <span className="mono text-[0.7rem] font-semibold text-green">{formatTime(dayBestMs)}</span>
                    )}
                    <span className="text-[0.7rem] text-text-muted min-w-[28px] text-right">{group.sessions.length}</span>
                  </span>
                </div>
              </div>

              {!collapsed && (
                <div className="pl-5 bg-bg-surface/30">
                  {group.sessions.map(s => {
                    const isBestOfDay = s.bestLapTimeMs > 0 && s.bestLapTimeMs === dayBestMs;
                    return (
                      <div
                        key={s.id}
                        className="flex items-center gap-2 px-4 py-1.5 cursor-pointer hover:bg-bg-hover transition-colors"
                        onClick={() => toggleSession(s.id)}
                      >
                        <input
                          type="checkbox"
                          checked={selectedIds.has(s.id)}
                          onChange={() => toggleSession(s.id)}
                          className="w-3.5 h-3.5 accent-accent cursor-pointer shrink-0"
                          onClick={e => e.stopPropagation()}
                        />
                        <span className="mono text-[0.72rem] text-text-secondary min-w-[48px]">{s.time}</span>
                        <span className="text-[0.72rem] text-text-muted truncate">{s.driverName}</span>
                        <span className="ml-auto flex items-center gap-3">
                          {s.bestLapTimeMs > 0 && (
                            <span className={`mono text-[0.7rem] ${isBestOfDay ? 'text-green font-semibold' : 'text-text-secondary'}`}>
                              {formatTime(s.bestLapTimeMs)}
                            </span>
                          )}
                          <span className="text-[0.72rem] text-text-muted min-w-[28px] text-right">{s.completedLaps}L</span>
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
